/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { InjectionToken } from '@angular/core';
import { deployNebulaClusterStarted } from '@app/cluster-management/ngrx/cluster-management.actions';
import { clusterUsers } from '@app/cluster-management/ngrx/cluster-user-management.actions';
import { MAX_ERRORS } from '@app/common/constants';
import {
  connectionToken,
  ggccLicenseStatus,
  setErrorSnackbarDuration,
  setSnackbarDuration,
  setTheme,
  splitAreaSizeModel,
} from '@app/core/ngrx/actions';
import {
  cluster3xConfiguration,
  cluster3xDistributionZones,
  cluster3xLocalPartitionStates,
  cluster3xTables,
  cluster3xTopology,
  clusterConnectorsAction,
} from '@app/core/ngrx/actions/cluster-3x.actions';
import { nebulaPlans } from '@app/core/ngrx/actions/nebula-plans.actions';
import { AppState, Themes } from '@app/core/types';
import { chooseCurrentCluster } from '@app/domain/cluster';
import { secondsToMilliseconds } from '@app/domain/time';
import { QUERIES_GROUP_NAME } from '@app/sql/domain/query';
import { removeQueryOk } from '@app/sql/ngrx/queries.actions';
import { mergeReducer } from '@common/utils/merge-reducer';
import { routerNavigatedAction, routerReducer } from '@ngrx/router-store';
import { Action, ActionReducer, ActionReducerMap, MetaReducer, createReducer, on } from '@ngrx/store';
import { localStorageSync } from 'ngrx-store-localstorage';
import { environment } from 'src/environments/environment';
import {
  attachClusterOk,
  authConfig,
  caches,
  changeThemeAccordingToOS,
  clearErrorLog,
  clusterTopology,
  clusters,
  currentClusterChange,
  dashboard,
  dashboardGroup,
  decLoading,
  error,
  getUserSuccess,
  incLoading,
  login,
  logoutSuccess,
  removeDashboardOk,
  setFilterHasData,
  setFilterVisibility,
  toggleBanner,
  toggleFilterVisibility,
  toggleTheme,
} from './actions';
import { columnVisibilityModel, sizeModel, sortModel } from './actions/ag-grid.actions';
import { teamList } from './actions/teams.actions';
import { tutorialCompletedAction } from './actions/tutorial.actions';
import { cardsReducer } from './billing-method.reducers';
import { dashboardTemplatesReducer, lastOpenedDashboardReducer } from './dashboard.reducers';
import { metricDefsReducer } from './metric-definition.reducers';

const removeNonexistentItems = (state: Map<string, any>, action: ReturnType<typeof clusters>) => {
  const clusterIds = action.data.map((c) => c.id);
  const removedClusterIds = [...state.keys()].filter((id) => !clusterIds.includes(id));
  if (!removedClusterIds.length) return state;
  const newState = new Map(state);
  removedClusterIds.forEach((id) => newState.delete(id));
  return newState;
};

export const loadingReducer = createReducer<AppState['loading']>(
  new Map<string, number>(),
  on(incLoading, (state, action) => new Map(state).set(action.key, (state.get(action.key) || 0) + 1)),
  on(decLoading, (state, action) => {
    const count = state.get(action.key);
    if (count === undefined) return state;
    const newState = new Map(state);
    if (action.force || count === 1) newState.delete(action.key);
    else newState.set(action.key, count - 1);
    return newState;
  }),
  on(routerNavigatedAction, (state, action) => new Map()),
);

export const userReducer = createReducer<AppState['user']>(
  null,
  on(login, getUserSuccess, (state, action) => action.user),
);

export const errorActionsReducer = <T extends Action>(state: AppState['errorActions'], action: T) => {
  if (action.type === clearErrorLog.type) return [];
  return 'error' in action || action.type === error.type
    ? [...state.slice(-MAX_ERRORS), { action, time: new Date() }]
    : state;
};

const DEFAULT_NOTIFICATION_DURATION = {
  error: secondsToMilliseconds(8),
  notification: secondsToMilliseconds(3),
};
export const snackbarDurationReducer = createReducer<AppState['snackbarDuration']>(
  DEFAULT_NOTIFICATION_DURATION,
  on(setErrorSnackbarDuration, (state, action) => ({ ...state, error: action.duration })),
  on(setSnackbarDuration, (state, action) => ({ ...state, notification: action.duration })),
);

export const clustersReducer = createReducer<AppState['clusters']>(
  [],
  on(clusters, (state, action) => action.data),
  on(attachClusterOk, (state, action) =>
    state.find((cluster) => cluster.id === action.cluster.id) ? state : [...state, action.cluster],
  ),
);

export const currentClusterReducer = createReducer<AppState['currentCluster']>(
  null,
  on(clusters, (state, action) => chooseCurrentCluster(state, action.data)),
  on(currentClusterChange, (state, action) => action.id),
  on(deployNebulaClusterStarted, (state, action) => action.data.clusterId),
);

export const clusterTopologyReducer = createReducer<AppState['clusterTopology']>(
  new Map(),
  on(clusterTopology, (state, action) => new Map(state).set(action.clusterId, action.data)),
  on(clusters, removeNonexistentItems),
);
export const cluster3xTopologyReducer = createReducer<AppState['cluster3xTopology']>(
  new Map(),
  on(cluster3xTopology, (state, action) => new Map(state).set(action.clusterId, action.data)),
  on(clusters, removeNonexistentItems),
);

export const cluster3xConfigurationReducer = createReducer<AppState['cluster3xConfiguration']>(
  new Map(),
  on(cluster3xConfiguration, (state, action) => new Map(state).set(action.clusterId, action.configuration)),
  on(clusters, removeNonexistentItems),
);

export const cluster3xTablesReducer = createReducer<AppState['cluster3xTables']>(
  new Map(),
  on(cluster3xTables, (state, action) => new Map(state).set(action.clusterId, action.data)),
  on(clusters, removeNonexistentItems),
);

export const cluster3xDistributionZonesReducer = createReducer<AppState['cluster3xDistributionZones']>(
  new Map(),
  on(cluster3xDistributionZones, (state, action) => new Map(state).set(action.clusterId, action.data)),
  on(clusters, removeNonexistentItems),
);

export const cluster3xLocalPartitionStatesReducer = createReducer<AppState['cluster3xLocalPartitionStates']>(
  new Map(),
  on(cluster3xLocalPartitionStates, (state, action) => new Map(state).set(action.clusterId, action.data)),
  on(clusters, removeNonexistentItems),
);

export const clusterConnectorsReducer = createReducer<AppState['clusterConnectors']>(
  [],
  on(clusterConnectorsAction, (state, action) => action.data),
);

export const clustersLoadedReducer = createReducer<AppState['clustersLoaded']>(
  false,
  on(clusters, (state, action) => true),
);

export const themeReducer = createReducer<AppState['theme']>(
  null,
  on(toggleTheme, (state) => (state === Themes.DARK ? Themes.LIGHT : Themes.DARK)),
  on(changeThemeAccordingToOS, (state, action) => (!state ? action.theme : state)),
  on(setTheme, (state, action) => action.theme),
);

export const dashboardGroupsReducer = createReducer<AppState['dashboardGroups']>(
  {},
  on(dashboardGroup, (state, action) => ({
    ...state,
    [action.dashboardGroup.name]: action.dashboardGroup,
  })),
);

export const dashboardsReducer = createReducer<AppState['dashboards']>(
  {},
  on(dashboard, (state, action) => ({
    ...state,
    [action.dashboard.id]: action.dashboard,
  })),
  on(removeDashboardOk, (state, action) => {
    const { [action.dashboardId]: value, ...newState } = state;
    return newState;
  }),
);

export const cachesReducer = createReducer<AppState['caches']>(
  new Map(),
  on(caches, (state, action) => new Map(state).set(action.clusterId, action.data)),
  on(clusters, removeNonexistentItems),
);

export const closedBannersReducer = createReducer<AppState['closedBanners']>(
  [],
  on(toggleBanner, (state, { opened, id }) => {
    const isIdClosed = state.includes(id);
    if ((opened && !isIdClosed) || (!opened && isIdClosed)) return state;

    return opened ? state.filter((it) => it !== id) : [...state, id];
  }),
);

export const sortModelsReducer = createReducer<AppState['sortModels']>(
  {},
  on(sortModel, (state, action) => ({ ...state, [action.key]: action.model })),
);

export const sizeModelsReducer = createReducer<AppState['sizeModels']>(
  {},
  on(sizeModel, (state, action) => ({ ...state, [action.key]: action.model })),
);

export const splitAreaSizeModelsReducer = createReducer<AppState['splitAreaSizeModels']>(
  {},
  on(splitAreaSizeModel, (state, action) => ({ ...state, [action.key]: action.model })),
  // clean up orphan query area size models
  on(dashboardGroup, (state, action) => {
    let newState = state;
    if (action.dashboardGroup.name === QUERIES_GROUP_NAME) {
      Object.keys(state).forEach((id: string) => {
        if (!action.dashboardGroup.workspaceIds.includes(id)) {
          const { [id]: value, ...rest } = newState;
          newState = rest;
        }
      });
    }
    return newState;
  }),
  on(removeQueryOk, (state, action) => {
    const { [action.id]: value, ...newState } = state;
    return newState;
  }),
);

export const columnVisibilityModelsReducer = createReducer<AppState['columnVisibilityModels']>(
  {},
  on(columnVisibilityModel, (state, action) => ({ ...state, [action.key]: action.model })),
);

export const licenseStatusReducer = createReducer<AppState['licenseStatus']>(
  null,
  on(ggccLicenseStatus, (state, action) => action.payload),
);

export const authConfigReducer = createReducer<AppState['authConfig']>(
  null,
  on(authConfig, (state, action) => action.config),
);

export const teamsReducer = createReducer<AppState['teams']>(
  null,
  on(teamList, (state, action) => action.data),
);

export const nebulaSubscriptionReducer = createReducer<AppState['nebulaSubscription']>(
  null,
  on(nebulaPlans, (state, { data }) => data),
);

export const clusterUsersReducer = createReducer<AppState['clusterUsers']>(
  new Map(),
  on(clusterUsers, (state, action) => new Map(state).set(action.clusterId, action.users)),
);

export const filterPanelReducer = createReducer<AppState['filterPanels']>(
  {},
  on(setFilterHasData, (state, action) => {
    const item = state[action.name];
    return {
      ...state,
      [action.name]: {
        hasData: action.hasData,
        visible: item ? item.visible : true,
        type: item ? item.type : 'auto',
      },
    };
  }),
  on(setFilterVisibility, (state, action) => {
    const item = state[action.name];
    return {
      ...state,
      [action.name]: {
        hasData: item ? item.hasData : false,
        visible: action.visible,
        type: item ? item.type : 'auto',
      },
    };
  }),
  on(toggleFilterVisibility, (state, action) => {
    const item = state[action.name];
    return {
      ...state,
      [action.name]: item
        ? { hasData: item.hasData, visible: !item.visible, type: 'manual' }
        : { hasData: false, visible: true, type: 'manual' },
    };
  }),
);

export const tutorialsReducer = createReducer<AppState['completedTutorials']>(
  [],
  on(tutorialCompletedAction, (state, action) => [...state, action.id]),
);

export const connectionTokenReducer = createReducer<AppState['connectionToken']>(
  null,
  on(connectionToken, (state, { connectionToken }) => connectionToken || null),
);

export const targetReducer = createReducer<AppState['target']>(environment.ggccTarget);

export function localStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  const keys: (keyof AppState)[] = [
    'snackbarDuration',
    'errorActions',
    'currentCluster',
    'theme',
    'closedBanners',
    'sortModels',
    'sizeModels',
    'splitAreaSizeModels',
    'columnVisibilityModels',
    'lastOpenedDashboard',
    'completedTutorials',
  ];
  return localStorageSync({
    keys,
    rehydrate: true,
    removeOnUndefined: true,
    mergeReducer,
  })(reducer);
}

export function sessionStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  const keys: (keyof AppState)[] = ['connectionToken'];
  return localStorageSync({
    keys,
    rehydrate: true,
    removeOnUndefined: true,
    storage: sessionStorage,
    mergeReducer,
  })(reducer);
}

export function makeInitialState(state?: AppState): AppState {
  return {
    theme: state ? state.theme : null,
    loading: new Map(),
    user: null,
    snackbarDuration: DEFAULT_NOTIFICATION_DURATION,
    errorActions: [],
    clusters: [],
    currentCluster: null,
    dashboardGroups: {},
    dashboards: {},
    dashboardTemplates: [],
    lastOpenedDashboard: {},
    blockNotifications: false,
    clustersLoaded: false,
    clusterTopology: new Map(),
    cluster3xTopology: new Map(),
    cluster3xConfiguration: new Map(),
    router: (state || ({} as AppState)).router,
    caches: new Map(),
    metricDefs: {},
    closedBanners: [],
    sortModels: {},
    sizeModels: {},
    splitAreaSizeModels: {},
    columnVisibilityModels: {},
    licenseStatus: null,
    authConfig: state?.authConfig ?? null,
    teams: null,
    nebulaSubscription: null,
    filterPanels: {},
    billingCards: [],
    clusterUsers: new Map(),
    connectionToken: null,
    target: state?.target ?? window.GGCC_TARGET,
    completedTutorials: state?.completedTutorials ?? [],
    cluster3xTables: new Map(),
    cluster3xDistributionZones: new Map(),
    cluster3xLocalPartitionStates: new Map(),
    clusterConnectors: [],
  };
}

export function clearState(reducer: ActionReducer<AppState>): ActionReducer<AppState> {
  return (state, action) => {
    if (action.type === logoutSuccess.type) state = makeInitialState(state);
    return reducer(state, action);
  };
}

export const appReducersMap = {
  loading: loadingReducer,
  user: userReducer,
  errorActions: errorActionsReducer,
  snackbarDuration: snackbarDurationReducer,
  clusters: clustersReducer,
  currentCluster: currentClusterReducer,
  clustersLoaded: clustersLoadedReducer,
  dashboardGroups: dashboardGroupsReducer,
  dashboards: dashboardsReducer,
  dashboardTemplates: dashboardTemplatesReducer,
  lastOpenedDashboard: lastOpenedDashboardReducer,
  router: routerReducer,
  clusterTopology: clusterTopologyReducer,
  cluster3xTopology: cluster3xTopologyReducer,
  cluster3xConfiguration: cluster3xConfigurationReducer,
  cluster3xTables: cluster3xTablesReducer,
  cluster3xDistributionZones: cluster3xDistributionZonesReducer,
  cluster3xLocalPartitionStates: cluster3xLocalPartitionStatesReducer,
  clusterConnectors: clusterConnectorsReducer,
  theme: themeReducer,
  caches: cachesReducer,
  closedBanners: closedBannersReducer,
  sortModels: sortModelsReducer,
  sizeModels: sizeModelsReducer,
  splitAreaSizeModels: splitAreaSizeModelsReducer,
  columnVisibilityModels: columnVisibilityModelsReducer,
  metricDefs: metricDefsReducer,
  licenseStatus: licenseStatusReducer,
  authConfig: authConfigReducer,
  teams: teamsReducer,
  nebulaSubscription: nebulaSubscriptionReducer,
  filterPanels: filterPanelReducer,
  billingCards: cardsReducer,
  clusterUsers: clusterUsersReducer,
  connectionToken: connectionTokenReducer,
  target: targetReducer,
  completedTutorials: tutorialsReducer,
} as ActionReducerMap<AppState, Action>;

export const APP_REDUCERS = new InjectionToken<ActionReducerMap<AppState, Action>>('App reducers');

export const metaReducers: MetaReducer<AppState>[] = [localStorageSyncReducer, sessionStorageSyncReducer, clearState];
